import { useCallback, useEffect, useState } from "react";
import { getFormQuestions } from "../../../API/applicationAPI";
import { Category, FormStructure } from "../../../types/form";
import {
  collectDescendantCategoryIds,
  findCategoryPath,
  findQuestionAnywhereInStructure,
  resolveLeafCategory,
} from "../formNavigatorUtils";
import { getVisibleLeafCategories } from "../../../utils/categoryVisibility";

export const useFormData = (formId: number | undefined) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formStructure, setFormStructure] = useState<FormStructure | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!formId) return;
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await getFormQuestions(formId);

        setFormStructure(data as FormStructure);

        const roots =
          data.categoryTree?.length > 0
            ? data.categoryTree
            : data.categories ?? [];

        if (roots.length > 0) {
          const visibleLeaves = getVisibleLeafCategories(roots, {});
          const firstSection = visibleLeaves[0] ?? resolveLeafCategory(roots[0]);
          const path = findCategoryPath(roots, firstSection.id);

          if (path?.length) {
            setExpandedCategories(
              new Set(path.slice(0, -1).map((c) => c.id))
            );
          } else if (data.categoryTree?.length) {
            setExpandedCategories(new Set([data.categoryTree[0].id]));
          }

          setActiveCategory(firstSection);
        }
      } catch (err: any) {
        setLoadError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load form questions"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
  }, [formId]);

  /** Accordion: one open root at a time; under each parent, only one branch with children stays expanded. */
  const handleToggleExpand = useCallback(
    (categoryId: number) => {
      if (!formStructure?.categoryTree?.length) {
        setExpandedCategories((prev) => {
          const next = new Set(prev);
          if (next.has(categoryId)) next.delete(categoryId);
          else next.add(categoryId);
          return next;
        });
        return;
      }

      const roots = formStructure.categoryTree;
      const rootIdSet = new Set(roots.map((r) => r.id));

      setExpandedCategories((prev) => {
        const next = new Set(prev);

        if (rootIdSet.has(categoryId)) {
          const rootNode = roots.find((r) => r.id === categoryId)!;
          if (next.has(categoryId)) {
            next.delete(categoryId);
            for (const id of collectDescendantCategoryIds(rootNode)) {
              if (id !== categoryId) next.delete(id);
            }
          } else {
            for (const r of roots) {
              for (const id of collectDescendantCategoryIds(r)) {
                next.delete(id);
              }
            }
            next.add(categoryId);
          }
          return next;
        }

        const path = findCategoryPath(roots, categoryId);
        if (!path?.length) {
          if (next.has(categoryId)) next.delete(categoryId);
          else next.add(categoryId);
          return next;
        }

        const clicked = path[path.length - 1];
        const parent = path.length >= 2 ? path[path.length - 2] : null;

        if (!parent?.subcategories?.length) {
          if (next.has(categoryId)) next.delete(categoryId);
          else next.add(categoryId);
          return next;
        }

        if (next.has(categoryId)) {
          next.delete(categoryId);
          for (const id of collectDescendantCategoryIds(clicked)) {
            if (id !== categoryId) next.delete(id);
          }
        } else {
          for (const sib of parent.subcategories) {
            for (const id of collectDescendantCategoryIds(sib)) {
              next.delete(id);
            }
          }
          next.add(categoryId);
        }
        return next;
      });
    },
    [formStructure]
  );

  const handleSelectCategory = (category: Category) => {
    setActiveCategory(category);
  };

  /**
   * Programmatic navigation (e.g. AI): expand path and open a section.
   * Parent id → first nested leaf; leaf id → that section (matches sidebar behavior).
   */
  const selectCategoryById = useCallback(
    (categoryId: number): boolean => {
      if (!formStructure) return false;
      const roots =
        formStructure.categoryTree?.length > 0
          ? formStructure.categoryTree
          : formStructure.categories ?? [];
      if (roots.length === 0) return false;

      const path = findCategoryPath(roots, categoryId);
      if (!path?.length) return false;

      const clicked = path[path.length - 1];
      const target =
        clicked.subcategories && clicked.subcategories.length > 0
          ? resolveLeafCategory(clicked)
          : clicked;
      const targetPath = findCategoryPath(roots, target.id);
      if (!targetPath?.length) return false;

      setExpandedCategories(() => {
        const next = new Set<number>();
        for (const c of targetPath.slice(0, -1)) {
          next.add(c.id);
        }
        return next;
      });
      setActiveCategory(target);
      return true;
    },
    [formStructure]
  );

  /** Open the exact category that lists this question (for scroll / cross-section tools). */
  const navigateToCategoryContainingQuestion = useCallback(
    (questionId: number): boolean => {
      if (!formStructure) return false;
      const roots =
        formStructure.categoryTree?.length > 0
          ? formStructure.categoryTree
          : formStructure.categories ?? [];
      if (roots.length === 0) return false;

      const hit = findQuestionAnywhereInStructure(formStructure, questionId);
      if (!hit || hit.category.id < 0) return false;

      const path = findCategoryPath(roots, hit.category.id);
      if (!path?.length) return false;

      setExpandedCategories(() => {
        const next = new Set<number>();
        for (const c of path.slice(0, -1)) {
          next.add(c.id);
        }
        return next;
      });
      setActiveCategory(hit.category);
      return true;
    },
    [formStructure]
  );

  return {
    isLoading,
    loadError,
    formStructure,
    activeCategory,
    expandedCategories,
    setActiveCategory,
    handleToggleExpand,
    handleSelectCategory,
    selectCategoryById,
    navigateToCategoryContainingQuestion,
  };
};
